import { useMemo, useState } from 'react'
import { Select } from '@mantine/core'
import { IconSearch } from '@tabler/icons-react'
import type { CareJourneysData } from './journeys'

/** Characters required before the picker offers anything. */
const PATIENT_SEARCH_MIN = 2

function idOf(reference: string): string {
  return reference.split('/').pop() ?? reference
}

/**
 * Patient picker for the Care Journeys board.
 *
 * A picker, not a text match: FHIR display names carry doubled spaces (an empty
 * middle name), prefixes and suffixes, so typing a patient's real full name
 * could silently match nothing — "Simon Brown" never found "Simon  Brown". The
 * value emitted is always a real patient id.
 *
 * The list stays EMPTY until two characters are typed. A clinic has hundreds of
 * patients in scope, and dumping all of them into a dropdown on focus is
 * unusable — and slow — at that size.
 *
 * Lives in the toolbar rather than the filter drawer: finding one patient is the
 * single most common thing a care coordinator does here, and behind Filters it
 * cost a drawer open, a search, an Apply and a close.
 */
export const JourneyPatientSelect = ({
  patients,
  value,
  onChange,
  className,
}: {
  patients: CareJourneysData['filterOptions']['patients'] | undefined
  value: string
  onChange: (patientId: string) => void
  className?: string
}) => {
  const [search, setSearch] = useState('')

  // Matching ignores whitespace runs so a stored "Simon  Brown" is still found
  // by typing "Simon Brown", and `limit` on the Select caps what renders.
  const options = useMemo(() => {
    const query = search.trim().toLowerCase().replace(/\s+/g, ' ')
    if (query.length < PATIENT_SEARCH_MIN) return []
    return (patients ?? [])
      .map((person) => ({ value: idOf(person.reference), label: person.display }))
      .filter((option) => option.label.toLowerCase().replace(/\s+/g, ' ').includes(query))
  }, [patients, search])

  const selectedLabel = useMemo(
    () => (patients ?? []).find((person) => idOf(person.reference) === value)?.display ?? '',
    [patients, value],
  )

  return (
    <Select
      className={className}
      aria-label="Find a patient"
      placeholder="Find a patient"
      searchable
      clearable
      limit={20}
      searchValue={search}
      onSearchChange={setSearch}
      // Focusing clears the query so a second search starts fresh. Mantine
      // keeps the selected label IN the search box, so typing "Mike" with Nora
      // selected inserted mid-string — "Nora VancMikee", matching nobody — and
      // you had to clear before searching again. Blur restores the label so a
      // selected patient still reads as selected.
      onFocus={() => setSearch('')}
      onBlur={() => setSearch(selectedLabel)}
      nothingFoundMessage={
        search.trim().length < PATIENT_SEARCH_MIN
          ? `Type ${PATIENT_SEARCH_MIN} letters to search`
          : 'No matching patient in this date range'
      }
      data={options}
      value={value || null}
      onChange={(next) => onChange(next ?? '')}
      leftSection={<IconSearch size={15} />}
    />
  )
}
